import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { Box, IconButton, Modal, ModalProps, Typography } from '@mui/material';

import { useConfirm } from 'src/contexts/ConfirmContext';
import { TestIds } from 'src/testIds';
import { Customer } from 'src/types';

import { NeedsAssessmentForm } from './NeedsAssessmentForm';

import useStyles from './NeedsAssessment.styles';

const CloseIconTestId = TestIds.components.needsAssessmentForm.closeIcon;

interface Props extends Partial<ModalProps> {
  agreedActivitiesEnabled: boolean;
  agreedActivitiesHourlyRateEnabled: boolean;
  customer: Customer;
  open: boolean;
  handleCancel: () => void;
  handleClose: () => void;
}

export const NeedsAssessmentModal: FC<Props> = ({
  agreedActivitiesEnabled,
  agreedActivitiesHourlyRateEnabled,
  customer,
  handleCancel,
  handleClose,
  open,
  ...props
}) => {
  const { closeButton, form, modal } = useStyles();
  const confirm = useConfirm();
  const { t } = useTranslation('translation');

  return (
    <Modal open={open} {...props}>
      <Box
        className={modal}
        sx={
          agreedActivitiesEnabled
            ? {
                boxSizing: 'border-box',
                maxWidth: '100vw',
                overflowX: 'hidden',
                overflowY: 'auto',
                width: '100vw',
              }
            : undefined
        }
      >
        <Box
          className={form}
          sx={
            agreedActivitiesEnabled
              ? { boxSizing: 'border-box', minWidth: 0, width: '100%' }
              : undefined
          }
        >
          <IconButton
            data-test-id={CloseIconTestId}
            className={closeButton}
            onClick={() => {
              confirm({
                content: (
                  <Typography>
                    {t('NeedsAssessmentForm.ConfirmModal.text')}
                  </Typography>
                ),
                hideCloseButton: true,
                primaryText: t('General.confirm'),
                secondaryText: t('General.abort'),
              })
                .then(handleCancel)
                // Avoid getting uncaught onPromise error
                .catch(() => {});
            }}
          >
            <Close />
          </IconButton>
          <NeedsAssessmentForm
            afterSubmit={handleClose}
            agreedActivitiesEnabled={agreedActivitiesEnabled}
            agreedActivitiesHourlyRateEnabled={
              agreedActivitiesHourlyRateEnabled
            }
            customer={customer}
          />
        </Box>
      </Box>
    </Modal>
  );
};
